import { useState } from 'react';
import { useNavigate, Link, useSearchParams} from 'react-router-dom';
import axios from 'axios';
import styles from './ForgotPasswordPage.module.css';
import global from '../styles/global.module.css';
import { formatPhone } from '../utils/phoneUtils';
import { useModal } from '../context/ModalContext';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';


const BASE_URL = `${API_URL}/api`;

function ForgotPasswordPage() {
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'findEmail');

    return (
        <div className={styles.container}>
            <div className={styles.box}>
                <h1 className={global.pageLogo}>SHOP</h1>

                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'findEmail' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('findEmail')}>
                        아이디 찾기
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'findPassword' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('findPassword')}>
                        비밀번호 찾기
                    </button>
                </div>

                {activeTab === 'findEmail' && <FindEmailTab />}
                {activeTab === 'findPassword' && <FindPasswordTab />}

                <div className={global.divider} />
                <Link to="/login" className={global.btnOutline}>로그인으로 돌아가기</Link>
            </div>
        </div>
    );
}

/* =====================
   아이디 찾기
===================== */
function FindEmailTab() {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [result, setResult] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleFind = async () => {
        if (!name || !phone) { setError('이름과 전화번호를 입력해 주세요.'); return; }
        setLoading(true);
        setError('');
        setResult('');
        try {
            const res = await axios.post(`${BASE_URL}/auth/find-email`, { name, phone });
            setResult(res.data.email);
        } catch (err) {
            setError(err.response?.data || '일치하는 회원 정보가 없습니다.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.stepContent}>
            <p className={styles.desc}>가입 시 등록한 이름과 전화번호를 입력해 주세요.</p>
            <input
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setError(''); }}
                className={global.input}
                placeholder="이름"
            />
            <input
                type="tel"
                value={phone}
                onChange={e => { setPhone(formatPhone(e.target.value)); setError(''); }}
                className={global.input}
                placeholder="010-0000-0000"
                maxLength={13}
            />
            {error && <p className={global.errorText}>{error}</p>}
            {result && (
                <div className={styles.resultBox}>
                    <p className={styles.resultLabel}>회원님의 아이디</p>
                    <p className={styles.resultEmail}>{result}</p>
                </div>
            )}
            <button className={global.btnPrimary} onClick={handleFind} disabled={loading}>
                {loading ? '조회 중...' : '아이디 찾기'}
            </button>
        </div>
    );
}

/* =====================
   비밀번호 찾기
===================== */
function FindPasswordTab() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const { showAlert } = useModal();

    const handleSendCode = async () => {
        if (!email) { setError('이메일을 입력해 주세요.'); return; }
        setLoading(true);
        setError('');
        try {
            await axios.post(`${BASE_URL}/email/send-reset`, { email });
            setStep(2);
            await showAlert('인증 코드가 발송되었습니다. 이메일을 확인해 주세요.', 'success');
        } catch (err) {
            setError(err.response?.data || '이메일 발송에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        if (!code) { setError('인증 코드를 입력해 주세요.'); return; }
        setLoading(true);
        setError('');
        try {
            const res = await axios.post(`${BASE_URL}/email/verify`, { email, code });
            if (res.data.verified) setStep(3);
        } catch {
            setError('인증 코드가 올바르지 않습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword) { setError('새 비밀번호를 입력해 주세요.'); return; }
        if (newPassword !== newPasswordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
        if (newPassword.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
        setLoading(true);
        setError('');
        try {
            await axios.post(`${BASE_URL}/auth/reset-password`, { email, newPassword });
            setSuccess('비밀번호가 재설정되었습니다.');
            setTimeout(() => navigate('/login'), 1500);
        } catch {
            setError('비밀번호 재설정에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className={styles.steps}>
                {['이메일 확인', '코드 인증', '비밀번호 설정'].map((label, i) => (
                    <div key={i} className={`${styles.step} ${step === i + 1 ? styles.stepActive : ''} ${step > i + 1 ? styles.stepDone : ''}`}>
                        <div className={styles.stepCircle}>{step > i + 1 ? '✓' : i + 1}</div>
                        <span className={styles.stepLabel}>{label}</span>
                    </div>
                ))}
            </div>

            {error && <p className={global.errorText}>{error}</p>}
            {success && <p className={global.successText}>{success}</p>}

            {step === 1 && (
                <div className={styles.stepContent}>
                    <p className={styles.desc}>가입하신 이메일 주소를 입력해 주세요.</p>
                    <input type="email" value={email}
                        onChange={e => { setEmail(e.target.value); setError(''); }}
                        className={global.input} placeholder="이메일" />
                    <button className={global.btnPrimary} onClick={handleSendCode} disabled={loading}>
                        {loading ? '발송 중...' : '인증 코드 발송'}
                    </button>
                </div>
            )}

            {step === 2 && (
                <div className={styles.stepContent}>
                    <p className={styles.desc}><strong>{email}</strong>로 발송된 인증 코드를 입력해 주세요.</p>
                    <input type="text" value={code}
                        onChange={e => { setCode(e.target.value); setError(''); }}
                        className={global.input} placeholder="인증 코드 6자리" maxLength={6} />
                    <button className={global.btnPrimary} onClick={handleVerifyCode} disabled={loading}>
                        {loading ? '확인 중...' : '인증 확인'}
                    </button>
                    <button className={global.btnOutline} onClick={handleSendCode} disabled={loading}>
                        코드 재발송
                    </button>
                </div>
            )}

            {step === 3 && (
                <div className={styles.stepContent}>
                    <p className={styles.desc}>새로운 비밀번호를 입력해 주세요.</p>
                    <input type="password" value={newPassword}
                        onChange={e => { setNewPassword(e.target.value); setError(''); }}
                        className={global.input} placeholder="새 비밀번호 (6자 이상)" />
                    <input type="password" value={newPasswordConfirm}
                        onChange={e => { setNewPasswordConfirm(e.target.value); setError(''); }}
                        className={global.input} placeholder="새 비밀번호 확인" />
                    <button className={global.btnPrimary} onClick={handleResetPassword} disabled={loading}>
                        {loading ? '처리 중...' : '비밀번호 재설정'}
                    </button>
                </div>
            )}
        </div>
    );
}

export default ForgotPasswordPage;